import React, { useState, useEffect, useCallback } from 'react';
import './MerchantAnalytics.css';

const MerchantAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [sortBy, setSortBy] = useState('revenue');
  const [viewMode, setViewMode] = useState('overview'); // overview, merchant-detail, top-performers

  // Fetch platform overview
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `/api/analytics/comprehensive/admin/overview?period=${period}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  // Fetch specific merchant analytics
  const fetchMerchantAnalytics = useCallback(async (merchantId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `/api/analytics/comprehensive/admin/merchant/${merchantId}?period=${period}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch merchant analytics');
      }

      const data = await response.json();
      setSelectedMerchant(data.data);
      setViewMode('merchant-detail');
    } catch (err) {
      console.error('Error fetching merchant analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  // Fetch top performers
  const fetchTopPerformers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `/api/analytics/comprehensive/admin/top-merchants?period=${period}&limit=20&sortBy=${sortBy}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch top performers');
      }

      const data = await response.json();
      setAnalytics(data.data);
      setViewMode('top-performers');
    } catch (err) {
      console.error('Error fetching top performers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, sortBy]);

  useEffect(() => {
    if (viewMode === 'overview') {
      fetchOverview();
    }
  }, [viewMode, fetchOverview]);

  // Format currency - uses merchant's currency when viewing specific merchant, defaults to NGN
  const formatCurrency = (value, currency = null) => {
    if (value === null || value === undefined) return '₦0.00';
    const currencyCode = currency || selectedMerchant?.merchant?.currency || 'NGN';
    try {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(value));
    } catch (e) {
      // Fallback if currency code is invalid
      return `${currencyCode} ${parseFloat(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  };

  // Format number
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return parseFloat(value).toLocaleString();
  };

  // Get KYC badge info
  const getKYCBadge = (kyc) => {
    if (!kyc) return { text: 'Not Started', class: 'kyc-not-started', level: 'LEVEL_0' };

    const statusClasses = {
      APPROVED: 'kyc-approved',
      PENDING_REVIEW: 'kyc-pending',
      REJECTED: 'kyc-rejected',
      NOT_STARTED: 'kyc-not-started'
    };

    const levelTexts = {
      LEVEL_0: 'L0',
      LEVEL_1: 'L1',
      LEVEL_2: 'L2',
      LEVEL_3: 'L3'
    };

    return {
      text: `${levelTexts[kyc.level] || 'L0'} - ${kyc.status === 'APPROVED' ? 'Approved' : kyc.status === 'PENDING_REVIEW' ? 'Pending' : kyc.status === 'REJECTED' ? 'Rejected' : 'Not Started'}`,
      class: statusClasses[kyc.status] || 'kyc-not-started',
      level: kyc.level,
      canWithdraw: kyc.canWithdrawFunds
    };
  };

  // Back to overview
  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedMerchant(null);
  };

  if (error) {
    return (
      <div className="merchant-analytics">
        <div className="analytics-error">
          <h2>Error Loading Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchOverview} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="merchant-analytics">
      {/* Header */}
      <div className="analytics-header">
        <div>
          <h1 className="analytics-title">Merchant Analytics</h1>
          <p className="analytics-subtitle">Platform-wide performance insights</p>
        </div>
        <div className="analytics-header-actions">
          {/* Period Selector */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="period-selector"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>

          {/* View Mode Buttons */}
          <div className="view-mode-buttons">
            <button
              className={`view-mode-btn ${viewMode === 'overview' ? 'active' : ''}`}
              onClick={() => setViewMode('overview')}
            >
              Overview
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'top-performers' ? 'active' : ''}`}
              onClick={fetchTopPerformers}
            >
              Top Performers
            </button>
          </div>
        </div>
      </div>

      {/* Platform Overview */}
      {viewMode === 'overview' && (
        <>
          {/* Platform Totals */}
          <div className="platform-totals">
            <div className="total-card">
              <div className="total-icon" style={{ background: '#D1FAE5', color: '#065F46' }}>
                💰
              </div>
              <div className="total-info">
                <p className="total-label">Total Revenue</p>
                <p className="total-value">
                  {loading ? '...' : formatCurrency(analytics?.platformTotals?.totalRevenue || 0)}
                </p>
              </div>
            </div>

            <div className="total-card">
              <div className="total-icon" style={{ background: '#DBEAFE', color: '#1E40AF' }}>
                📦
              </div>
              <div className="total-info">
                <p className="total-label">Total Orders</p>
                <p className="total-value">
                  {loading ? '...' : formatNumber(analytics?.platformTotals?.totalOrders || 0)}
                </p>
              </div>
            </div>

            <div className="total-card">
              <div className="total-icon" style={{ background: '#EDE9FE', color: '#6B21A8' }}>
                👥
              </div>
              <div className="total-info">
                <p className="total-label">Total Customers</p>
                <p className="total-value">
                  {loading ? '...' : formatNumber(analytics?.platformTotals?.totalCustomers || 0)}
                </p>
              </div>
            </div>

            <div className="total-card">
              <div className="total-icon" style={{ background: '#FEF3C7', color: '#92400E' }}>
                🏪
              </div>
              <div className="total-info">
                <p className="total-label">Active Merchants</p>
                <p className="total-value">
                  {loading ? '...' : formatNumber(analytics?.platformTotals?.activeMerchants || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Merchants Table */}
          <div className="analytics-card">
            <div className="card-header">
              <h2>All Merchants</h2>
              <p>Click on a merchant to view detailed analytics</p>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="table-skeleton">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="skeleton-row"></div>
                  ))}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="merchants-table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Revenue</th>
                        <th>Orders</th>
                        <th>KYC</th>
                        <th>Settlements</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics?.merchants?.map((merchant, index) => {
                        const data = merchant.analytics;
                        const isActive = data?.sales?.current?.totalOrders > 0;
                        const kycBadge = getKYCBadge(merchant.kyc);
                        return (
                          <tr key={index}>
                            <td>
                              <div className="merchant-cell">
                                <div className="merchant-avatar">
                                  {merchant.merchant.username?.charAt(0).toUpperCase() || 'M'}
                                </div>
                                <div className="merchant-info">
                                  <p className="merchant-name">{merchant.merchant.username}</p>
                                  <p className="merchant-email">{merchant.merchant.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="revenue-cell">
                              {formatCurrency(data?.revenue?.current?.totalRevenue || 0)}
                            </td>
                            <td>{formatNumber(data?.sales?.current?.totalOrders || 0)}</td>
                            <td>
                              <span className={`kyc-badge ${kycBadge.class}`} title={kycBadge.canWithdraw ? 'Can withdraw funds' : 'Cannot withdraw funds'}>
                                {kycBadge.text}
                                {kycBadge.canWithdraw && <span className="withdrawal-indicator"> ✓</span>}
                              </span>
                            </td>
                            <td className="settlement-cell">
                              {merchant.settlements ? (
                                <div className="settlement-info">
                                  <div className="settlement-amount">{formatCurrency(merchant.settlements.totalAmount)}</div>
                                  <div className="settlement-count">{merchant.settlements.total} total{merchant.settlements.pending > 0 && ` (${merchant.settlements.pending} pending)`}</div>
                                </div>
                              ) : (
                                <span className="text-muted">No data</span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                                {isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <button
                                onClick={() => fetchMerchantAnalytics(merchant.merchant.id)}
                                className="view-btn"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Top Performers View */}
      {viewMode === 'top-performers' && (
        <div className="analytics-card">
          <div className="card-header">
            <h2>Top Performing Merchants</h2>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-selector"
            >
              <option value="revenue">Sort by Revenue</option>
              <option value="orders">Sort by Orders</option>
              <option value="customers">Sort by Customers</option>
              <option value="aov">Sort by AOV</option>
            </select>
          </div>
          <div className="card-content">
            {loading ? (
              <div className="table-skeleton">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="skeleton-row"></div>
                ))}
              </div>
            ) : (
              <div className="leaderboard">
                {analytics?.topMerchants?.map((merchant, index) => (
                  <div key={index} className="leaderboard-item">
                    <div className="leaderboard-rank">
                      <span className={`rank-badge rank-${index + 1}`}>#{index + 1}</span>
                    </div>
                    <div className="leaderboard-merchant">
                      <div className="merchant-avatar large">
                        {merchant.merchant.username?.charAt(0).toUpperCase() || 'M'}
                      </div>
                      <div className="merchant-info">
                        <p className="merchant-name">{merchant.merchant.username}</p>
                        <p className="merchant-email">{merchant.merchant.email}</p>
                      </div>
                    </div>
                    <div className="leaderboard-stats">
                      <div className="stat">
                        <span className="stat-label">Revenue</span>
                        <span className="stat-value">{formatCurrency(merchant.revenue)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Orders</span>
                        <span className="stat-value">{formatNumber(merchant.orders)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Customers</span>
                        <span className="stat-value">{formatNumber(merchant.customers)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">AOV</span>
                        <span className="stat-value">{formatCurrency(merchant.aov)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => fetchMerchantAnalytics(merchant.merchant.id)}
                      className="view-details-btn"
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Merchant Detail View */}
      {viewMode === 'merchant-detail' && (
        <div className="merchant-detail">
          <button onClick={handleBackToOverview} className="back-btn">
            ← Back to Overview
          </button>

          {loading ? (
            <div className="detail-skeleton">
              <div className="skeleton-header">
                <div className="skeleton-avatar"></div>
                <div className="skeleton-text-block">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line"></div>
                </div>
              </div>
              <div className="skeleton-kpis">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="skeleton-kpi"></div>
                ))}
              </div>
            </div>
          ) : selectedMerchant ? (
            <>
              <div className="merchant-detail-header">
                <div className="merchant-avatar xlarge">
                  {selectedMerchant.merchant.username?.charAt(0).toUpperCase() || 'M'}
                </div>
                <div>
                  <h2>{selectedMerchant.merchant.username}</h2>
                  <p>{selectedMerchant.merchant.email}</p>
                  <p className="merchant-since">
                    Member since {new Date(selectedMerchant.merchant.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Merchant KPIs */}
              <div className="merchant-kpis">
            <div className="kpi-card">
              <p className="kpi-label">Total Revenue</p>
              <p className="kpi-value">
                {formatCurrency(selectedMerchant.analytics?.revenue?.current?.totalRevenue || 0)}
              </p>
              {selectedMerchant.analytics?.revenue?.change?.totalRevenue !== null && selectedMerchant.analytics?.revenue?.change?.totalRevenue !== undefined && (
                <p className={`kpi-change ${selectedMerchant.analytics?.revenue?.change?.totalRevenue >= 0 ? 'positive' : 'negative'}`}>
                  {selectedMerchant.analytics?.revenue?.change?.totalRevenue >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(selectedMerchant.analytics?.revenue?.change?.totalRevenue).toFixed(1)}%
                </p>
              )}
            </div>

            <div className="kpi-card">
              <p className="kpi-label">Total Orders</p>
              <p className="kpi-value">
                {formatNumber(selectedMerchant.analytics?.sales?.current?.totalOrders || 0)}
              </p>
              {selectedMerchant.analytics?.sales?.change?.totalOrders !== null && selectedMerchant.analytics?.sales?.change?.totalOrders !== undefined && (
                <p className={`kpi-change ${selectedMerchant.analytics?.sales?.change?.totalOrders >= 0 ? 'positive' : 'negative'}`}>
                  {selectedMerchant.analytics?.sales?.change?.totalOrders >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(selectedMerchant.analytics?.sales?.change?.totalOrders).toFixed(1)}%
                </p>
              )}
            </div>

            <div className="kpi-card">
              <p className="kpi-label">Customers</p>
              <p className="kpi-value">
                {formatNumber(selectedMerchant.analytics?.customers?.current?.totalCustomers || 0)}
              </p>
              {selectedMerchant.analytics?.customers?.change?.totalCustomers !== null && selectedMerchant.analytics?.customers?.change?.totalCustomers !== undefined && (
                <p className={`kpi-change ${selectedMerchant.analytics?.customers?.change?.totalCustomers >= 0 ? 'positive' : 'negative'}`}>
                  {selectedMerchant.analytics?.customers?.change?.totalCustomers >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(selectedMerchant.analytics?.customers?.change?.totalCustomers).toFixed(1)}%
                </p>
              )}
            </div>

            <div className="kpi-card">
              <p className="kpi-label">Average Order Value</p>
              <p className="kpi-value">
                {formatCurrency(selectedMerchant.analytics?.sales?.current?.averageOrderValue || 0)}
              </p>
              {selectedMerchant.analytics?.sales?.change?.averageOrderValue !== null && selectedMerchant.analytics?.sales?.change?.averageOrderValue !== undefined && (
                <p className={`kpi-change ${selectedMerchant.analytics?.sales?.change?.averageOrderValue >= 0 ? 'positive' : 'negative'}`}>
                  {selectedMerchant.analytics?.sales?.change?.averageOrderValue >= 0 ? '↑' : '↓'}{' '}
                  {Math.abs(selectedMerchant.analytics?.sales?.change?.averageOrderValue).toFixed(1)}%
                </p>
              )}
            </div>
          </div>

          {/* KYC & Settlement Information */}
          <div className="merchant-detail-sections">
            {/* KYC Section */}
            <div className="detail-section kyc-section">
              <h3>KYC Status</h3>
              {selectedMerchant.kyc ? (
                <div className="kyc-details">
                  <div className="kyc-detail-row">
                    <span className="label">Level:</span>
                    <span className={`kyc-badge ${getKYCBadge(selectedMerchant.kyc).class}`}>
                      {selectedMerchant.kyc.level.replace('LEVEL_', 'Level ')}
                    </span>
                  </div>
                  <div className="kyc-detail-row">
                    <span className="label">Status:</span>
                    <span className={`status-badge ${selectedMerchant.kyc.status === 'APPROVED' ? 'active' : 'inactive'}`}>
                      {selectedMerchant.kyc.status}
                    </span>
                  </div>
                  <div className="kyc-detail-row">
                    <span className="label">Can Receive Payments:</span>
                    <span>{selectedMerchant.kyc.canReceivePayments ? '✓ Yes' : '✗ No'}</span>
                  </div>
                  <div className="kyc-detail-row">
                    <span className="label">Can Withdraw Funds:</span>
                    <span>{selectedMerchant.kyc.canWithdrawFunds ? '✓ Yes' : '✗ No'}</span>
                  </div>
                  <div className="kyc-detail-row">
                    <span className="label">Daily Limit:</span>
                    <span>{formatCurrency(selectedMerchant.kyc.dailyTransactionLimit)}</span>
                  </div>
                  <div className="kyc-detail-row">
                    <span className="label">Monthly Limit:</span>
                    <span>{formatCurrency(selectedMerchant.kyc.monthlyTransactionLimit)}</span>
                  </div>
                  {selectedMerchant.kyc.accountVerified && (
                    <>
                      <div className="kyc-detail-row">
                        <span className="label">Bank Account:</span>
                        <span>✓ Verified</span>
                      </div>
                      {selectedMerchant.kyc.bankName && (
                        <div className="kyc-detail-row">
                          <span className="label">Bank:</span>
                          <span>{selectedMerchant.kyc.bankName}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p className="no-data">No KYC data available</p>
              )}
            </div>

            {/* Settlement Section */}
            <div className="detail-section settlement-section">
              <h3>Settlements</h3>
              {selectedMerchant.settlements ? (
                <div className="settlement-details">
                  <div className="settlement-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Settlements:</span>
                      <span className="summary-value">{selectedMerchant.settlements.total}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Amount:</span>
                      <span className="summary-value">{formatCurrency(selectedMerchant.settlements.totalMerchantAmount)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Service Charges:</span>
                      <span className="summary-value">{formatCurrency(selectedMerchant.settlements.totalServiceCharge)}</span>
                    </div>
                  </div>
                  {selectedMerchant.settlements.byStatus && Object.keys(selectedMerchant.settlements.byStatus).length > 0 && (
                    <div className="settlement-by-status">
                      <h4>By Status:</h4>
                      {Object.entries(selectedMerchant.settlements.byStatus).map(([status, data]) => (
                        <div key={status} className="status-row">
                          <span className={`status-badge ${status}`}>{status.toUpperCase()}</span>
                          <span>{data.count} ({formatCurrency(data.amount)})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="no-data">No settlement data available</p>
              )}
            </div>
          </div>
            </>
          ) : (
            <div className="no-data">
              <p>No merchant data available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MerchantAnalytics;
